// Platform.jsx
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import TenantNavbar from "../../TenantLanding/Navbar/tenantNavbar";

import {
  UserCircle, BarChart3, BookOpen, CheckSquare, ClipboardList, FileText,
  Map, AlertTriangle, Shield, Store, LayoutDashboard, ChevronRight, ArrowRight, Check,
  Layers, Workflow, FileWarning, ShoppingBag, GraduationCap, FolderTree,
  MapPin, LineChart, Users, Briefcase
} from 'lucide-react';
import Navbar from "../Navbar/tenantNavbar";
import Footer from "../../Footer/Footer";
import './Platform.css';

const site = 'https://your-platform.com';

const iconMap = {
  UserCircle, BarChart3, BookOpen, CheckSquare, ClipboardList, FileText,
  Map, AlertTriangle, Shield, Store, LayoutDashboard, Layers, Workflow,
  FileWarning, ShoppingBag, GraduationCap, FolderTree, MapPin, LineChart, Users, Briefcase
};

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function PlatformPage() {
  const [activeModule, setActiveModule] = useState(0);

  const platform = {
    hero: {
      eyebrow: 'PLATFORM FEATURES',
      headline: '11 Integrated Modules, One Unified Platform',
      subheadline: 'Every module purpose-built for the inspection and competency management lifecycle.'
    },
    modules: [
      {
        number: '01',
        title: 'Digital Surveyor Profile',
        tagline: 'Your professional identity, verified and visible.',
        icon: 'UserCircle',
        description: 'A comprehensive digital profile for surveyors and inspectors, showcasing certifications, experience, project history, and competency ratings—accessible to clients and employers.',
        capabilities: [
          'Verified certifications',
          'Project history',
          'Competency ratings',
          'Public profile'
        ],
        who_benefits: 'Surveyors, Inspectors, Employers'
      },
      {
        number: '02',
        title: 'Competency Framework',
        tagline: 'Defined standards for every role and level.',
        icon: 'BarChart3',
        description: 'A structured competency framework that defines the skills, knowledge, and behaviors required for each role and level within your organization.',
        capabilities: [
          'Role-based competencies',
          'Skill gap analysis',
          'Progression tracking',
          'Assessment tools'
        ],
        who_benefits: 'HR Teams, Managers, Employees'
      },
      {
        number: '03',
        title: 'Inspection Workflows',
        tagline: 'Streamlined inspection from start to finish.',
        icon: 'Workflow',
        description: 'End-to-end inspection workflows that guide surveyors through every step of the inspection process, from scheduling to final reporting.',
        capabilities: [
          'Automated workflows',
          'Checklist management',
          'Real-time updates',
          'Reporting tools'
        ],
        who_benefits: 'Surveyors, Inspectors, Project Managers'
      },
      {
        number: '04',
        title: 'NCR Management',
        tagline: 'Track and resolve non-conformances effectively.',
        icon: 'FileWarning',
        description: 'Comprehensive non-conformance reporting (NCR) management system that helps you track, assign, and resolve issues efficiently.',
        capabilities: [
          'NCR tracking',
          'Assignment management',
          'Resolution workflows',
          'Audit trails'
        ],
        who_benefits: 'Quality Managers, Inspectors, Project Teams'
      },
      {
        number: '05',
        title: 'Technical Services Marketplace',
        tagline: 'Connect talent with opportunity.',
        icon: 'ShoppingBag',
        description: 'A marketplace that connects technical service providers with clients who need specialized inspection and surveying services.',
        capabilities: [
          'Service provider profiles',
          'Job matching',
          'Project bidding',
          'Rating system'
        ],
        who_benefits: 'Service Providers, Clients, Project Managers'
      },
      {
        number: '06',
        title: 'Training & Certification Hub',
        tagline: 'Build competence through structured learning.',
        icon: 'GraduationCap',
        description: 'A centralized hub for training programs, certifications, and continuing education to ensure your team stays current with industry standards.',
        capabilities: [
          'Training programs',
          'Certification tracking',
          'Learning paths',
          'Digital badges'
        ],
        who_benefits: 'Employees, HR Teams, Training Providers'
      },
      {
        number: '07',
        title: 'Project Management',
        tagline: 'Manage inspection projects with ease.',
        icon: 'Briefcase',
        description: 'Comprehensive project management tools designed specifically for inspection and surveying projects, from planning to completion.',
        capabilities: [
          'Project planning',
          'Resource allocation',
          'Timeline tracking',
          'Budget management'
        ],
        who_benefits: 'Project Managers, Team Leads, Clients'
      },
      {
        number: '08',
        title: 'Document Management',
        tagline: 'All your critical documents in one place.',
        icon: 'FolderTree',
        description: 'Secure, organized document management system that keeps all your critical inspection documents, reports, and records accessible and version-controlled.',
        capabilities: [
          'Document repository',
          'Version control',
          'Access management',
          'Search and retrieval'
        ],
        who_benefits: 'All Users, Administrators, Project Teams'
      },
      {
        number: '09',
        title: 'Mapping & Assets',
        tagline: 'Visualize assets and inspection locations.',
        icon: 'MapPin',
        description: 'Interactive mapping and asset management tools that help you visualize inspection locations, assets, and project sites in real-time.',
        capabilities: [
          'Interactive maps',
          'Asset tracking',
          'Geolocation',
          'Site visualization'
        ],
        who_benefits: 'Surveyors, Asset Managers, Project Teams'
      },
      {
        number: '10',
        title: 'Reporting & Analytics',
        tagline: 'Data-driven insights at your fingertips.',
        icon: 'LineChart',
        description: 'Powerful reporting and analytics tools that transform your inspection data into actionable insights for better decision-making.',
        capabilities: [
          'Custom dashboards',
          'Performance metrics',
          'Trend analysis',
          'Export capabilities'
        ],
        who_benefits: 'Managers, Executives, Quality Teams'
      },
      {
        number: '11',
        title: 'Client Portal',
        tagline: 'Transparency and trust, delivered.',
        icon: 'Users',
        description: 'A dedicated client portal that provides real-time access to project updates, inspection reports, and compliance documentation.',
        capabilities: [
          'Real-time updates',
          'Report access',
          'Document sharing',
          'Communication tools'
        ],
        who_benefits: 'Clients, Project Managers, Stakeholders'
      }
    ],
    cta: {
      headline: 'Ready to Transform Your Operations?',
      subheadline: 'Join Qualion today and experience the power of an integrated platform.',
      cta_primary: 'Get Started',
      cta_secondary: 'Partner With Us'
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${site}/platform#webpage`,
    name: 'Platform Features — 11 Integrated Modules | Professional Development Platform',
    url: `${site}/platform`,
    isPartOf: { '@id': `${site}/#website` },
    about: { '@id': `${site}/#organization` },
  };

  return (
    <>
      <Helmet>
        <title>Platform Features — 11 Integrated Modules | Professional Development Platform</title>
        <meta name="description" content="Explore our 11 integrated platform modules — from Digital Surveyor Profiles and Competency Frameworks to Inspection Workflows, NCR Management, and the Technical Services Marketplace." />
        <link rel="canonical" href={`${site}/platform`} />
        <meta property="og:title" content="Platform Features — 11 Integrated Modules | Professional Development Platform" />
        <meta property="og:description" content="Every module purpose-built for the inspection and competency management lifecycle." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${site}/platform`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main>
        {/* ── HERO ── */}
        <section className="platform-hero">
          <div className="platform-hero-top-line" />
          <div className="platform-hero-grid-overlay" />
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="platform-hero-content"
            >
              <div className="platform-hero-eyebrow">
                <div className="platform-hero-line" />
                <span>{platform.hero.eyebrow}</span>
              </div>
              <h1 className="platform-hero-title">{platform.hero.headline}</h1>
              <p className="platform-hero-subtitle">{platform.hero.subheadline}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="platform-module-strip"
            >
              {platform.modules.map((mod, i) => (
                <button
                  key={i}
                  onClick={() => setActiveModule(i)}
                  className={`platform-strip-btn ${activeModule === i ? 'active' : ''}`}
                >
                  {mod.number}
                </button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── MODULE DEEP-DIVE ── */}
        <section className="platform-modules-section">
          <div className="container">
            <div className="platform-modules-grid">
              {/* Sidebar nav */}
              <div className="platform-modules-sidebar">
                <div className="platform-modules-nav">
                  {platform.modules.map((mod, i) => {
                    const Icon = iconMap[mod.icon] || Shield;
                    return (
                      <button
                        key={i}
                        onClick={() => setActiveModule(i)}
                        className={`platform-nav-btn ${activeModule === i ? 'active' : ''}`}
                      >
                        <span className={`platform-nav-number ${activeModule === i ? 'active' : ''}`}>
                          {mod.number}
                        </span>
                        <Icon size={14} className={`platform-nav-icon ${activeModule === i ? 'active' : ''}`} />
                        <span className="platform-nav-title">{mod.title}</span>
                        {activeModule === i && <ChevronRight size={12} className="platform-nav-arrow" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Module detail */}
              <div className="platform-module-detail">
                <motion.div
                  key={activeModule}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {(() => {
                    const mod = platform.modules[activeModule];
                    const Icon = iconMap[mod.icon] || Shield;
                    return (
                      <div>
                        <div className="platform-module-header">
                          <div className="platform-module-icon-box">
                            <Icon size={28} className="platform-module-icon" />
                          </div>
                          <div>
                            <div className="platform-module-badge">
                              <span>MODULE {mod.number}</span>
                            </div>
                            <h2 className="platform-module-title">{mod.title}</h2>
                            <p className="platform-module-tagline">{mod.tagline}</p>
                          </div>
                        </div>

                        <p className="platform-module-description">{mod.description}</p>

                        <div className="platform-capabilities">
                          <h3 className="platform-capabilities-title">Key Capabilities</h3>
                          <div className="platform-capabilities-grid">
                            {mod.capabilities.map((cap, j) => (
                              <div key={j} className="platform-capability-item">
                                <div className="platform-capability-check">
                                  <Check size={11} />
                                </div>
                                <span>{cap}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="platform-benefits-box">
                          <div>
                            <div className="platform-benefits-label">Who Benefits</div>
                            <div className="platform-benefits-text">{mod.who_benefits}</div>
                          </div>
                          <div className="platform-dots">
                            {platform.modules.map((_, i) => (
                              <button
                                key={i}
                                onClick={() => setActiveModule(i)}
                                className={`platform-dot ${i === activeModule ? 'active' : ''}`}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="platform-nav-buttons">
                          <button
                            onClick={() => setActiveModule(Math.max(0, activeModule - 1))}
                            disabled={activeModule === 0}
                            className="platform-nav-prev"
                          >
                            <ChevronRight size={14} className="rotate-180" />
                            Previous module
                          </button>
                          <button
                            onClick={() => setActiveModule(Math.min(platform.modules.length - 1, activeModule + 1))}
                            disabled={activeModule === platform.modules.length - 1}
                            className="platform-nav-next"
                          >
                            Next module
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* ── ALL MODULES GRID ── */}
        <section className="platform-grid-section">
          <div className="container">
            <FadeUp className="platform-grid-header">
              <h2>All 11 Modules at a Glance</h2>
              <p>Click any module to explore it in detail above.</p>
            </FadeUp>
            <div className="platform-grid">
              {platform.modules.map((mod, i) => {
                const Icon = iconMap[mod.icon] || Shield;
                return (
                  <FadeUp key={i} delay={i * 0.04}>
                    <motion.button
                      whileHover={{ y: -3 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => { setActiveModule(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className={`platform-grid-card ${activeModule === i ? 'active' : ''}`}
                    >
                      <div className="platform-grid-card-header">
                        <span className={`platform-grid-card-number ${activeModule === i ? 'active' : ''}`}>
                          {mod.number}
                        </span>
                        <div className={`platform-grid-card-icon ${activeModule === i ? 'active' : ''}`}>
                          <Icon size={15} />
                        </div>
                      </div>
                      <h3 className={`platform-grid-card-title ${activeModule === i ? 'active' : ''}`}>
                        {mod.title}
                      </h3>
                      <p className={`platform-grid-card-tagline ${activeModule === i ? 'active' : ''}`}>
                        {mod.tagline}
                      </p>
                    </motion.button>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="platform-cta">
          <div className="platform-cta-top-line" />
          <div className="container">
            <FadeUp>
              <div className="platform-cta-content">
                <h2>{platform.cta.headline}</h2>
                <p>{platform.cta.subheadline}</p>
                <div className="platform-cta-buttons">
                  <a href="/join-ecosystem" className="platform-cta-primary">
                    {platform.cta.cta_primary} <ArrowRight size={16} />
                  </a>
                  <a href="/about" className="platform-cta-secondary">
                    {platform.cta.cta_secondary} <ChevronRight size={16} />
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>

      {/* <Footer /> */}
    </>
  );
}